import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute } from "@tanstack/react-router";
import { LegalLayout, LegalSection } from "@/components/legal-layout";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — the Vibing Company®" },
      { name: "description", content: "Terms governing personalized services delivered by the Vibing Company®." },
      { property: "og:title", content: "Terms of Service — the Vibing Company®" },
      ...ogImageMeta("terms", "Share card: terms of service"),
      { property: "og:description", content: "Terms governing personalized services delivered by the Vibing Company®." },
    ],
    links: [{ rel: "canonical", href: "/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalLayout kicker="04.TERMS" title="Terms of Service" updated="June 2026">
      <LegalSection label="01" title="Who we are">
        <p>
          These Terms of Service ("Terms") govern your use of the website vibing.inc and the
          services delivered by the Vibing Company® ("we", "us", "our"), a sole proprietorship
          of Jonathan Osburg, St.-Klara-Str. 7A, 82377 Penzberg, Germany. By engaging us, you
          ("Client") accept these Terms.
        </p>
      </LegalSection>

      <LegalSection label="02" title="Services we offer">
        <p>
          We provide personalized design, development, advisory, rescue and migration services.
          The shape, timing, deliverables and commercial terms of each engagement are agreed with
          the Client before work begins.
        </p>
        <p>
          The exact scope of each engagement is the one shown at checkout and confirmed in the
          intake conversation. Anything not explicitly listed is out of scope.
        </p>
      </LegalSection>

      <LegalSection label="03" title="Ongoing engagements">
        <p>
          If an engagement includes recurring work or billing, its cadence, scope and cancellation
          terms are stated in the proposal or agreement accepted by the Client. You may also write
          to <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a> with questions.
        </p>
        <p>
          Unused capacity, revisions and deliverables are handled according to the agreed proposal.
        </p>
      </LegalSection>

      <LegalSection label="04" title="Project engagements">
        <p>
          Project engagements begin after the agreed payment and required information have been
          received. Any estimated timing assumes timely Client cooperation, including access,
          content and approvals.
        </p>
      </LegalSection>

      <LegalSection label="05" title="Payment & taxes">
        <p>
          All payments are processed by Stripe. Prices are shown in USD and exclude applicable
          taxes unless stated. Where Stripe Tax applies to your jurisdiction, the correct tax is
          calculated and shown at checkout.
        </p>
        <p>
          Operator is a small business per § 19 UStG and does not charge German VAT on its own
          invoices.
        </p>
      </LegalSection>

      <LegalSection label="06" title="Client responsibilities">
        <p>You agree to:</p>
        <ul className="list-none space-y-2">
          <li>— Provide accurate contact and billing details</li>
          <li>— Grant the access we reasonably need (repository, hosting, accounts)</li>
          <li>— Respond to questions and approvals in a timely manner</li>
          <li>— Own (or have the right to use) any content and assets you supply</li>
          <li>— Use the services lawfully and not for prohibited content (see § 12)</li>
        </ul>
      </LegalSection>

      <LegalSection label="07" title="Intellectual property">
        <p>
          On full payment of the relevant invoice, you receive a worldwide, perpetual licence to
          use the deliverables produced for you in your business. Pre-existing tools, libraries,
          internal templates, and know-how we use to deliver remain ours. We may reference the
          engagement and show non-confidential work in our portfolio unless you ask us in writing
          not to.
        </p>
      </LegalSection>

      <LegalSection label="08" title="Confidentiality">
        <p>
          Both sides agree to keep non-public information shared during the engagement
          confidential and to use it only to deliver the services. This obligation survives
          termination.
        </p>
      </LegalSection>

      <LegalSection label="09" title="Warranties & disclaimer">
        <p>
          We deliver the services with professional care and skill. To the maximum extent
          permitted by law, the services are otherwise provided "as is" without further warranty
          of any kind, including merchantability, fitness for a particular purpose, or
          non-infringement. We do not warrant that the services will be uninterrupted, error-free,
          or meet specific business outcomes.
        </p>
      </LegalSection>

      <LegalSection label="10" title="Limitation of liability">
        <p>
          To the maximum extent permitted by applicable law, our total liability for any claim
          arising from or related to the services is limited to the amounts paid by you to us
          during the three (3) months preceding the event giving rise to the claim. We are not
          liable for indirect, incidental, special, consequential, or punitive damages, or for
          lost profits, revenue, data, or goodwill. Nothing in these Terms excludes liability
          that cannot be excluded by law (including liability for intent or gross negligence,
          injury to life, body, or health).
        </p>
      </LegalSection>

      <LegalSection label="11" title="Refunds & cancellation">
        <p>
          Refund eligibility and cancellation are described in our{" "}
          <a href="/refunds" className="underline">Refund Policy</a>, which forms part of these
          Terms.
        </p>
      </LegalSection>

      <LegalSection label="12" title="Acceptable use">
        <p>
          We do not work on projects that promote illegal activity, harassment, hateful content,
          adult content involving minors, weapons, surveillance of individuals without consent, or
          anything that violates Stripe's acceptable-use policy. We may decline or terminate any
          engagement that, in our reasonable judgement, falls into these categories.
        </p>
      </LegalSection>

      <LegalSection label="13" title="Termination">
        <p>
          Either party may terminate an ongoing engagement according to its agreement. We may
          suspend or terminate immediately if you breach these Terms, fail to pay, or use the
          services in violation of § 12. On termination we hand over completed deliverables and
          stop billing future periods.
        </p>
      </LegalSection>

      <LegalSection label="14" title="Changes">
        <p>
          We may update these Terms as the service evolves. Material changes will be announced on
          this page with a new "Last updated" date and, for active subscribers, notified by email
          before they take effect where applicable.
        </p>
      </LegalSection>

      <LegalSection label="15" title="Governing law & venue">
        <p>
          These Terms are governed by the laws of the Federal Republic of Germany, excluding the
          UN Convention on Contracts for the International Sale of Goods. Exclusive place of
          jurisdiction for disputes with merchants is Munich, Germany. Mandatory consumer
          protection rights remain unaffected.
        </p>
      </LegalSection>

      <LegalSection label="16" title="Contact">
        <p>
          Questions about these Terms:{" "}
          <a href="mailto:jon@vibing.inc" className="underline">jon@vibing.inc</a>.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
